import { actions } from './reducer'
import ssapi from 'lib/api/secondstepApi'
import {
  NetSuiteAsset,
  NetSuiteAssetFilters,
  AssignNetSuiteAssetRequest,
  BulkAssignUploadResults,
} from 'types'
import { AxiosResponse } from 'axios'
import { addNotification } from 'store/notifications'

export const searchNetSuiteAssets = (searchCriteria: NetSuiteAssetFilters) => {
  return async function (dispatch) {
    dispatch(actions.fetchingNetSuiteAssets())

    // Check if any criteria is provided
    const hasSearchCriteria = Object.values(searchCriteria).some(
      value => value !== undefined && value !== null && value !== '',
    )

    if (!hasSearchCriteria) {
      dispatch(
        actions.fetchingNetSuiteAssetsError('You must provide search criteria'),
      )
      return
    }

    // Get all non-empty criteria for display purposes
    const validCriteria = Object.entries(searchCriteria).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, value]) => value !== undefined && value !== null && value !== '',
    )

    // Dispatch setSearchQuery for each key-value pair
    validCriteria.forEach(([key, value]) => {
      dispatch(
        actions.setSearchQuery({
          key,
          value: String(value),
        }),
      )
    })

    try {
      const response = await ssapi.getNetSuiteAssets(searchCriteria)
      if (response.status === 204 || response.data.length === 0) {
        dispatch(actions.fetchingNetSuiteAssetsError('No assets found'))
      } else {
        const data = response?.data
        dispatch(actions.fetchingNetSuiteAssetsSuccess(data))
      }
    } catch (err) {
      dispatch(actions.fetchingNetSuiteAssetsError(err))
      addNotification({
        color: 'danger',
        text: err,
      })
    }
  }
}

export const clearNetSuiteAssets = () => {
  return async function (dispatch) {
    dispatch(actions.clearNetSuiteAssets())
    dispatch(actions.setSearchQuery(null))
  }
}

export const fetchNetSuiteAssetById = assetId => {
  return async dispatch => {
    dispatch(actions.fetchingNetSuiteAssetsSuccess())
    try {
      const response: AxiosResponse<NetSuiteAsset> =
        await ssapi.getNetSuiteAssetById(assetId)
      if (response.status === 204 || response.data === null) {
        dispatch(
          actions.fetchingNetSuiteAssetsError(
            `No assets found with ` + assetId,
          ),
        )
      } else {
        dispatch(actions.fetchingNetSuiteAssetsSuccess([response.data]))
      }
    } catch (err) {
      dispatch(actions.fetchingNetSuiteAssetsError(err))
      return false
    }
  }
}

export const updateNetSuiteAsset = (
  assetId: number,
  asset: AssignNetSuiteAssetRequest,
) => {
  return async dispatch => {
    dispatch(actions.updatingNetSuiteAsset())
    try {
      const response = await ssapi.updateNetSuiteAsset(assetId, asset)
      dispatch(actions.updatingNetSuiteAssetSuccess(response.data))
      dispatch(
        addNotification({
          color: 'success',
          text: 'License updated successfully',
        }),
      )
    } catch (err) {
      const errorMessage = err.response.data || 'Failed to update license'
      dispatch(actions.updatingNetSuiteAssetError(errorMessage))
      dispatch(
        addNotification({
          color: 'danger',
          text: String(errorMessage),
        }),
      )
      return false
    }
  }
}

export const handleBulkAssignUpload = (file: File) => {
  return async function (dispatch) {
    dispatch(actions.bulkAssignFileSubmitting(file))
    const bodyFormData = new FormData()
    bodyFormData.set('assetsFile', file)

    try {
      const response = await ssapi.patchNetSuiteAssets(bodyFormData)

      // The response structure is: { data: [], errors: [], error: null, success: bool }
      const uploadResults: BulkAssignUploadResults = {
        data: response.data.data || [],
        errors: response.data.errors,
        error: response.data.error || null,
        success: response.data.success || false,
      }

      dispatch(actions.bulkAssignFileSucceeded(uploadResults))
    } catch (error) {
      let errorMsg =
        "Something's gone wrong while uploading file. Please try again or file a ticket for assistance."

      // Extract error message from response
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data
        } else if (error.response.data?.errors?.length > 0) {
          // If we have an errors array with structured data, display on page only
          const uploadResults: BulkAssignUploadResults = {
            data: [],
            errors: error.response.data.errors || [],
            error: error.response.data.error || null,
            success: false,
          }
          dispatch(actions.bulkAssignFileSucceeded(uploadResults))
          return
        } else if (error.response.data?.error) {
          errorMsg = error.response.data.error
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message
        }
      }

      // Only show notification and error state for unexpected errors
      dispatch(actions.bulkAssignFileError(errorMsg))

      dispatch(
        addNotification({
          color: 'danger',
          text: errorMsg,
        }),
      )
    }
  }
}
